import { useId, useState, type FormEvent } from 'react';
import type { ProspectDto, UpdateProspectRequest } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PendingButton } from '@/components/ui/pending-button';

type Field = keyof Omit<UpdateProspectRequest, 'concurrencyVersion'>;
type Draft = Record<Field, string>;

// In the order the form shows them, each with its label and the grid columns it spans (of six).
const FIELDS: { field: Field; label: string; span: 2 | 3 | 6; type?: string }[] = [
  { field: 'firstName', label: 'First name', span: 2 },
  { field: 'middleName', label: 'Middle name', span: 2 },
  { field: 'lastName', label: 'Last name', span: 2 },
  { field: 'nameSuffix', label: 'Suffix', span: 2 },
  { field: 'phone', label: 'Phone', span: 2, type: 'tel' },
  { field: 'email', label: 'Email', span: 2, type: 'email' },
  { field: 'address', label: 'Street address', span: 6 },
  { field: 'addressLine2', label: 'Apt, suite, unit', span: 6 },
  { field: 'city', label: 'City', span: 2 },
  { field: 'state', label: 'State', span: 2 },
  { field: 'zipCode', label: 'ZIP code', span: 2 },
  { field: 'employer', label: 'Employer', span: 3 },
  { field: 'occupation', label: 'Occupation', span: 3 },
];

const SPAN: Record<2 | 3 | 6, string> = { 2: 'sm:col-span-2', 3: 'sm:col-span-3', 6: 'sm:col-span-6' };

function toDraft(prospect: ProspectDto): Draft {
  const draft = {} as Draft;
  for (const { field } of FIELDS) draft[field] = prospect[field] ?? '';
  return draft;
}

/** Every field, blank ones as null so the server clears them, with the token the next save sends. */
function toRequest(draft: Draft, concurrencyVersion: number): UpdateProspectRequest {
  const request = { concurrencyVersion } as UpdateProspectRequest;
  for (const { field } of FIELDS) {
    const value = draft[field].trim();
    request[field] = value === '' ? null : value;
  }
  return request;
}

interface ProspectDetailsFormProps {
  prospect: ProspectDto;
  onSave: (request: UpdateProspectRequest) => void;
  onCancel: () => void;
  isSaving: boolean;
  /** Why the last save was refused, as the server said it. */
  error: string | null;
}

/**
 * A Fundraiser's correction of one Prospect's details — name parts, phone, email, address, employer
 * and occupation. The edit changes only the Prospect: nothing a treasurer files. Presentational.
 */
export function ProspectDetailsForm({ prospect, onSave, onCancel, isSaving, error }: ProspectDetailsFormProps) {
  const idPrefix = useId();
  const [draft, setDraft] = useState<Draft>(() => toDraft(prospect));
  const [invalid, setInvalid] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (draft.firstName.trim() === '' && draft.lastName.trim() === '') {
      setInvalid('Enter a first or last name.');
      return;
    }
    setInvalid(null);
    onSave(toRequest(draft, prospect.concurrencyVersion));
  };

  const message = invalid ?? error;
  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
        {FIELDS.map(({ field, label, span, type }) => {
          const id = `${idPrefix}-${field}`;
          return (
            <div key={field} className={`space-y-1.5 ${SPAN[span]}`}>
              <Label htmlFor={id}>{label}</Label>
              <Input
                id={id}
                type={type ?? 'text'}
                value={draft[field]}
                onChange={(e) => setDraft((current) => ({ ...current, [field]: e.target.value }))}
              />
            </div>
          );
        })}
      </div>

      {message && (
        <p role="alert" className="text-sm text-destructive">
          {message}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <PendingButton type="submit" pending={isSaving}>
          Save details
        </PendingButton>
      </div>
    </form>
  );
}
